import React, { useState, useEffect, useRef } from 'react';
import { FaCrown, FaStar } from 'react-icons/fa';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  ExpressCheckoutElement,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { GoogleLogin } from '@react-oauth/google';
import { useWoodpeckerAuth } from './Woodpecker';
import { stripeApi } from '../api';
import Dialog from '../components/ui/Dialog';
import { Button } from '../components/ui/button';
import './PricingModal.css';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_WOODPECKER);

const PaymentForm = ({ selectedPlan, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    // This triggers form validation and wallet collection
    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message);
      setProcessing(false);
      return;
    }

    try {
      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
      });

      if (confirmError) {
        setError(confirmError.message);
      } else {
        onSuccess();
      }
    } catch (err) {
      setError('An unexpected error occurred.');
    }

    setProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="payment-form">
      <ExpressCheckoutElement 
        options={{
          paymentMethodOrder: ['apple_pay', 'google_pay', 'link', 'cashapp', 'amazon_pay']
        }}
      />
      {/* <h4 className="payment-divider">Or pay with card</h4> */}
      <div className="payment-element-wrapper">
        <PaymentElement />
      </div>
      {error && <div role="alert" className="my-4 rounded-xl bg-red-50 p-3 text-red-800">{error}</div>}
      <div className="mt-6 flex flex-wrap justify-end gap-3">
        <Button variant="neutral" size="action"
          type="button"
          onClick={onCancel}
          className="m-0 border-solid"
          disabled={processing}
        >
          Cancel
        </Button>
        <Button variant="brand" size="action"
          type="submit"
          className="m-0"
          disabled={!stripe || processing}
        >
          {processing ? 'Processing...' : 'Pay Now'}
        </Button>
      </div>
    </form>
  );
};

const PricingModal = ({ isOpen, onClose }) => {
  const [selectedPlan, setSelectedPlan] = useState('woodpeckerMonthly');
  const [clientSecret, setClientSecret] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const {
    idToken,
    authProvider,
    userEmail,
    userName,
    isPremium,
    isAuthenticated,
    handleGoogleLoginSuccess,
    handleGoogleLoginError,
  } = useWoodpeckerAuth();
  const [isCancelling, setIsCancelling] = useState(false);
  const modalRef = useRef(null);

  useEffect(() => {
    if (showPaymentForm && modalRef.current) {
      modalRef.current.scrollTop = 0;
    }
  }, [showPaymentForm]);

  const features = [
    { name: 'Training Sets', free: '1', premium: 'Unlimited' },
    { name: 'Max Training Set Size', free: '100 Puzzles', premium: '1000 Puzzles' },
    { name: 'Training Cycles', free: 'unlimited', premium: 'Unlimited' },
    { name: 'Stockfish Analysis', free: 'Yes', premium: 'Yes' },
    { name: 'Stockfish Refutation', free: 'No', premium: 'Yes' },
    { name: 'Performance Analytics', free: 'Yes', premium: 'Yes' },
    // { name: 'Generate Puzzles from Your Games', free: 'No', premium: 'Yes' },
  ];

  const plans = {
    woodpeckerMonthly: {
      price: 3.00,
      period: 'month',
      savings: null
    },
    woodpeckerYearly: {
      price: 24.00,
      period: 'year',
      savings: 'Save 33%'
    }
  };

  const handleUpgradeClick = async () => {
    setIsLoading(true);
    try {
      const { clientSecret: secret } = await stripeApi.createPaymentIntent(
        idToken,
        authProvider,
        selectedPlan,
        userEmail,
        userName,
      );
      setClientSecret(secret);
      setShowPaymentForm(true);
    } catch (error) {
      console.error('Error creating payment intent:', error);
      alert('Failed to initiate payment. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentSuccess = () => {
    setShowPaymentForm(false);
    onClose();
    // You might want to refresh the user's premium status here
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your current billing period.')) {
      return;
    }

    setIsCancelling(true);
    try {
      await stripeApi.cancelSubscription(idToken, authProvider, userEmail);
      alert('Your subscription has been cancelled successfully. You will retain access to premium features until the end of your current billing period.');
      onClose();
      // You might want to refresh the user's premium status here
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      alert('Failed to cancel subscription. Please try again or contact support.');
    } finally {
      setIsCancelling(false);
    }
  };

  const renderPremiumStatus = () => (
    <div className="mt-5 border-0 border-t border-solid border-amber-200 pt-5 text-amber-950">
      <div className="flex items-center justify-center gap-2">
        <FaCrown aria-hidden="true" className="shrink-0 text-amber-700" />
        <h3 className="m-0 text-lg font-semibold">Premium Member</h3>
      </div>
      <div className="mt-4">
        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-2">
            <FaStar className="benefit-icon" />
            <span>Unlimited Training Sets</span>
          </div>
          <div className="flex items-center gap-2">
            <FaStar className="benefit-icon" />
            <span>Unlimited Puzzle Size</span>
          </div>
          <div className="flex items-center gap-2">
            <FaStar className="benefit-icon" />
            <span>Unlimited Training Cycles</span>
          </div>
          <div className="flex items-center gap-2">
            <FaStar className="benefit-icon" />
            <span>Advanced Analytics</span>
          </div>
        </div>
        <div className="mt-5 text-center text-sm">
          <p>Thank you for supporting Puzzlify!</p>
          <Button variant="neutral" size="action"
            className="m-0 mt-4 border-solid text-red-800"
            onClick={handleCancelSubscription}
            disabled={isCancelling}
          >
            {isCancelling ? 'Cancelling...' : 'Cancel Subscription'}
          </Button>
        </div>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title={showPaymentForm ? 'Complete Your Payment' : 'Choose Your Plan'} className="max-w-4xl" contentRef={modalRef}>
        {isLoading && (
          <div role="status" className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/90 p-4 text-center">
            <div aria-hidden="true" className="h-10 w-10 rounded-full border-4 border-solid border-slate-200 border-t-sky-700 motion-safe:animate-spin"></div>
            <p>Loading payment options...</p>
          </div>
        )}
        {!showPaymentForm ? (
          <>
            <div className="pricing-header mb-5 md:mb-6">
              <p className="mt-1 mb-3 text-gray-600 text-base md:text-lg">
                Upgrade to Premium and accelerate your chess improvement
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Button size="action" variant={selectedPlan === 'woodpeckerMonthly' ? 'brand' : 'neutral'}
                className="m-0 border-solid" aria-pressed={selectedPlan === 'woodpeckerMonthly'}
                onClick={() => setSelectedPlan('woodpeckerMonthly')}
              >
                Monthly
              </Button>
              <Button size="action" variant={selectedPlan === 'woodpeckerYearly' ? 'brand' : 'neutral'}
                className="m-0 flex-col gap-0 border-solid" aria-pressed={selectedPlan === 'woodpeckerYearly'}
                onClick={() => setSelectedPlan('woodpeckerYearly')}
              >
                Yearly
                <span className="text-xs font-normal">Save 33%</span>
              </Button>
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <div className="min-w-0 rounded-2xl border border-solid border-slate-200 bg-slate-50 p-4 sm:p-6">
                <div className="plan-header">
                  <h3>Free</h3>
                  <div className="price">$0</div>
                  <p>Forever</p>
                </div>
                <ul className="m-0 list-none p-0">
                  {features.map((feature, index) => (
                    <li key={index} className="flex flex-wrap justify-between gap-x-3 gap-y-1 border-0 border-b border-solid border-slate-200 py-3 text-sm">
                      <span className="feature-name">{feature.name}</span>
                      <span className="feature-value">{feature.free}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="min-w-0 rounded-2xl border border-solid border-amber-300 bg-amber-50 p-4 sm:p-6">
                <div className="plan-header">
                  <div className="premium-badge">
                    <FaCrown />
                    Premium
                  </div>
                  <div className="price">
                    ${plans[selectedPlan].price}
                    <span className="period">/{plans[selectedPlan].period}</span>
                  </div>
                  {plans[selectedPlan].savings && (
                    <div className="savings">{plans[selectedPlan].savings}</div>
                  )}
                </div>
                <ul className="m-0 list-none p-0">
                  {features.map((feature, index) => (
                    <li key={index} className="flex flex-wrap justify-between gap-x-3 gap-y-1 border-0 border-b border-solid border-amber-200 py-3 text-sm">
                      <span className="feature-name">{feature.name}</span>
                      <span className="feature-value">{feature.premium}</span>
                    </li>
                  ))}
                </ul>
                {isPremium ? (
                  renderPremiumStatus()
                ) : !isAuthenticated ? (
                  <div className="google-login-container-modal">
                    <p className="signin-prompt">Please sign in to upgrade your plan.</p>
                    <GoogleLogin
                      onSuccess={handleGoogleLoginSuccess}
                      onError={handleGoogleLoginError}
                      theme="outline"
                      size="large"
                    />
                  </div>
                ) : (
                  <Button variant="brand" size="action" className="m-0 mt-6 w-full" disabled={isLoading} onClick={handleUpgradeClick}>
                    Upgrade Now
                  </Button>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="mx-auto max-w-lg">
            <p className="mb-6">You're just one step away from premium access!</p>
            {clientSecret && (
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <PaymentForm
                  selectedPlan={selectedPlan}
                  onSuccess={handlePaymentSuccess}
                  onCancel={() => setShowPaymentForm(false)}
                />
              </Elements>
            )}
          </div>
        )}
    </Dialog>
  );
};

export default PricingModal; 
